import {
  ARCHIVAL_STATUS_ARCHIVED,
  ARCHIVAL_STATUS_RESTORING,
} from 'js/constants/pipelineConstants';
import React from 'react';
import ArchivalStatus from 'new/elements/ArchivalStatus';
import {
  RuntimeStatusIndicator,
  RuntimeStatusIndicatorProps,
} from 'new/elements/DotsLoader/RuntimeStatusIndicator';
import LightningStatus from 'new/elements/LightningStatus';
import { Stack } from 'new/elements/Stack';
import type { ArchivalStatus as ArchivalStatusType } from 'src/@types';
import Tooltip from 'js/components/elements/mui/Tooltip';
import styles from './pipelineStatusChip.module.scss';

export enum Status {
  INIT = 'INIT',
  ACTIVE = 'ACTIVE',
  PAUSED = 'PAUSED',
  RATE_LIMITED = 'RATE_LIMITED',
}

type Props = {
  status: Status;
  size?: 'normal' | 'small';
  revertDirection?: boolean;
  id: number | string;
  /**
   * When ARCHIVED or RESTORING, only archival state is shown (no Active/Paused/Init). ARCHIVED also
   * gets an explanatory tooltip. Use for flows.
   */
  archivalStatus?: ArchivalStatusType;
  /**
   * The owning flow's archival state (the API reports it on each resource). When ARCHIVED, the
   * badge reads "Archived" like the flow does, and a tooltip shows the status the resource returns
   * to on restore. Use for sources, sinks and nexsets.
   */
  parentArchivalStatus?: ArchivalStatusType;
  /** When false, RuntimeStatusIndicator is not rendered. Default true. */
  showRuntimeIndicator?: boolean;
  className?: string;
} & RuntimeStatusIndicatorProps;

type ArchivedFlowTooltipContentProps = {
  message: string;
  /** Status the resource returns to on restore. Omitted for the flow itself. */
  restoreStatus?: Status;
};

const ArchivedFlowTooltipContent = ({
  message,
  restoreStatus,
}: ArchivedFlowTooltipContentProps) => (
  <div className={styles.archivedTooltip}>
    <b>Flow archived</b>
    <span>{message}</span>
    {restoreStatus && (
      <div className={styles.archivedTooltipFooter}>
        <span>Returns to</span>
        <span className={styles.archivedTooltipStatus}>
          <LightningStatus status={restoreStatus} size="small" />
        </span>
        <span>on restore</span>
      </div>
    )}
  </div>
);

type ArchivedBadgeWithTooltipProps = {
  id: number | string;
  size?: Props['size'];
  className: string;
  tooltip: React.ReactNode;
};

const ArchivedBadgeWithTooltip = ({
  id,
  size,
  className,
  tooltip,
}: ArchivedBadgeWithTooltipProps) => (
  <Tooltip tooltipTheme="general" mode="dark" arrow placement="top" title={tooltip}>
    {/* Focusable so keyboard and screen-reader users can reach the tooltip */}
    <div className={styles.archivedTrigger} tabIndex={0} data-testid={`archived-flow-status-${id}`}>
      <ArchivalStatus
        archivalStatus={ARCHIVAL_STATUS_ARCHIVED}
        id={id}
        size={size}
        className={className}
      />
    </div>
  </Tooltip>
);

const PipelineStatusChip = ({
  runtimeStatus,
  status,
  id,
  size,
  revertDirection,
  ingestionMode,
  hideInfo,
  archivalStatus,
  parentArchivalStatus,
  showRuntimeIndicator = true,
  className,
}: Props) => {
  const statusClassName = className ?? styles.status;

  if (archivalStatus === ARCHIVAL_STATUS_ARCHIVED) {
    return (
      <ArchivedBadgeWithTooltip
        id={id}
        size={size}
        className={statusClassName}
        tooltip={
          <ArchivedFlowTooltipContent message="This flow isn't processing while it's archived." />
        }
      />
    );
  }

  if (archivalStatus === ARCHIVAL_STATUS_RESTORING) {
    return (
      <ArchivalStatus
        archivalStatus={archivalStatus}
        id={id}
        size={size}
        className={statusClassName}
      />
    );
  }

  if (parentArchivalStatus === ARCHIVAL_STATUS_ARCHIVED) {
    return (
      <ArchivedBadgeWithTooltip
        id={id}
        size={size}
        className={statusClassName}
        tooltip={
          <ArchivedFlowTooltipContent
            message="This resource isn't processing while its flow is archived."
            restoreStatus={status}
          />
        }
      />
    );
  }

  return (
    <Stack
      direction={revertDirection ? 'row-reverse' : 'row'}
      alignItems="center"
      gap={size === 'small' ? '8px' : '16px'}
    >
      <LightningStatus size={size} id={id} className={statusClassName} status={status} />
      {showRuntimeIndicator && (
        <RuntimeStatusIndicator
          runtimeStatus={runtimeStatus}
          ingestionMode={ingestionMode}
          status={status}
          hideInfo={hideInfo}
        />
      )}
    </Stack>
  );
};

export default PipelineStatusChip;
